/**
 * Coach-side write path for ROSTER_ENTRY ratings. Inserts directly
 * into the `ratings` table with ratee_type='ROSTER_ENTRY' — see
 * migration 00085 + packages/core/src/rosterEntry/rating.ts.
 *
 * Batched per-corner insert: a single coach action produces up to 4
 * rows (one per corner) in a single Supabase insert call, which is
 * the natural unit for a "rate this kid tonight" flow.
 */
import { supabase } from './supabase';
import {
  validateRosterEntryRating,
  CORNER_IDS,
  type CornerId,
} from '@4cm/core/rosterEntry/rating';
import { useAuth } from '../contexts/AuthContext';

export type RatingsCrudResult =
  | { ok: true; insertedCount: number }
  | { ok: false; error: string };

export interface RateRosterEntryArgs {
  raterId: string;
  rateeId: string;
  /** Map from corner id → score. Undefined / null corners are skipped. */
  scoresByCorner: Partial<Record<CornerId, number | null>>;
  context: 'SESSION' | 'GAME';
  sessionId?: string | null;
  gameId?: string | null;
}

export async function rateRosterEntry(args: RateRosterEntryArgs): Promise<RatingsCrudResult> {
  const rows: Array<{
    rater_id: string;
    ratee_id: string;
    ratee_type: 'ROSTER_ENTRY';
    context: 'SESSION' | 'GAME';
    session_id: string | null;
    game_id: string | null;
    corner_id: CornerId;
    score: number;
    is_blind: boolean;
    state: 'REVEALED';
    revealed_at: string;
  }> = [];

  const now = new Date().toISOString();

  for (const corner of CORNER_IDS) {
    const score = args.scoresByCorner[corner];
    if (score == null) continue;
    const validation = validateRosterEntryRating({
      ratee_id: args.rateeId,
      corner_id: corner,
      score,
      context: args.context,
      session_id: args.sessionId ?? null,
      game_id: args.gameId ?? null,
    });
    if (!validation.ok) {
      return { ok: false, error: validation.errors[0]?.message ?? 'Invalid rating.' };
    }
    rows.push({
      rater_id: args.raterId,
      ratee_id: args.rateeId,
      ratee_type: 'ROSTER_ENTRY',
      context: args.context,
      session_id: args.sessionId ?? null,
      game_id: args.gameId ?? null,
      corner_id: corner,
      score,
      // Ghost players don't self-rate; the blind state machine doesn't
      // apply. Coach rating is posted directly + revealed immediately.
      is_blind: false,
      state: 'REVEALED',
      revealed_at: now,
    });
  }

  if (rows.length === 0) {
    return { ok: false, error: 'Set at least one corner score before saving.' };
  }

  const { error } = await supabase.from('ratings').insert(rows);
  if (error) return { ok: false, error: error.message };
  return { ok: true, insertedCount: rows.length };
}

/**
 * Hook-style helper that wraps `rateRosterEntry` with the current
 * coach's auth.uid() pulled from AuthContext. Returns a function so
 * the call site stays clean.
 */
export function useRateRosterEntry() {
  const { user } = useAuth();
  return async (args: Omit<RateRosterEntryArgs, 'raterId'>): Promise<RatingsCrudResult> => {
    if (!user?.id) return { ok: false, error: 'You must be signed in to rate a player.' };
    return rateRosterEntry({ ...args, raterId: user.id });
  };
}
